import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import { Grid, Button, Divider, Box } from "@mui/material";
import { ClientSettingName, IPairing } from "@vmosolutions/oc-interfaces";
import { SpinnerProgress } from "../../SpinnerProgress/SpinnerProgress";
import { useDispatch, useSelector } from "react-redux";
import { closeContextMenu } from "src/redux/actions/ContextMenu.Actions";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import { v1 } from "uuid";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { ShortCodeChip } from "../../ShortCodes/ShortCodeChip";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import moment from "moment-timezone";
import { ShortDescription } from "src/components/Pairings/ShortDescription";
import { getClientSetting, openSnackbar } from "src/redux/actions/App.Actions";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import { IModalInfo } from "src/pages/CrewSchedule/CrewScheduleWindow";
import { EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { PairingErrorTypes } from "src/components/DialogModal/PairingContextMenuModals/PairingErrorModal";

interface ISearchAvailableCrewPairingProps {
  id: string;
  path: string[];
  crewMember: ICrewMember;
  selectedTimes: number[];
  setActiveEventModal?: (modalInfo: IModalInfo) => void;
}

/** Available pairings bucketed by the day they start on, in the display timezone of the schedule. */
interface IPairingsOnDate {
  /** Sortable YYYY-MM-DD key for the day, used for grouping and as the React key */
  dateKey: string;
  /** The date as shown on the divider ("Wed, Sep 9, 2026") */
  label: string;
  pairings: IPairing[];
}

export const SearchAvailableCrewPairings = (props: ISearchAvailableCrewPairingProps): JSX.Element => {
  const { id, path, crewMember, selectedTimes, setActiveEventModal } = props;
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pairings, setPairings] = useState<IPairing[]>([]);
  const [descSetting, setDescSetting] = useState<boolean>(true);
  const selectedTimezone = useSelector(
    (state: IRootState) => state.crewSchedule?.windowProperties?.[id]?.displayTimezone
  );
  const times = useMemo(() => {
    if (selectedTimes?.length === 0) {
      return [];
    } else if (selectedTimes?.length === 1) {
      return selectedTimes;
    } else {
      return [Math.min(...selectedTimes), Math.max(...selectedTimes)];
    }
  }, [selectedTimes]);

  // A single selected day can only return pairings that start on that day, so the date dividers
  // would just be one redundant header. They only earn their space across a multi-day selection.
  const isMultiDay = selectedTimes?.length > 1;

  // When several days are selected the results cover the whole range, so split them into one
  // labelled section per start date rather than one undifferentiated list. Days break on the
  // timezone picked in the clock selector, so a divider lines up with the gantt column above it.
  const pairingsByDate = useMemo((): IPairingsOnDate[] => {
    const timezoneId = selectedTimezone?.timezoneId ?? "Etc/UTC";
    const dates: IPairingsOnDate[] = [];
    const datesByKey = new Map<string, IPairingsOnDate>();

    [...pairings]
      .sort(
        (a: IPairing, b: IPairing) =>
          moment.utc(a.start ?? a.approxStart).valueOf() - moment.utc(b.start ?? b.approxStart).valueOf()
      )
      .forEach((pairing: IPairing) => {
        const startOfPairing = moment.utc(pairing.start ?? pairing.approxStart).tz(timezoneId);
        const dateKey = startOfPairing.format("YYYY-MM-DD");
        let date = datesByKey.get(dateKey);
        if (!date) {
          date = { dateKey: dateKey, label: startOfPairing.format("ddd, MMM D, YYYY"), pairings: [] };
          datesByKey.set(dateKey, date);
          dates.push(date);
        }
        date.pairings.push(pairing);
      });

    return dates;
  }, [pairings, selectedTimezone]);

  const getAvailableCrewPairingsAction = useCallback(async (): Promise<void> => {
    if (times.length === 0) {
      return;
    }

    //get the actual date selected, them get the date in right timezone as epoch
    // Every selected time is a start-of-day epoch, so the last one has to be pushed out to the end
    // of its day. Querying up to the raw value ends the window at midnight and drops that day.
    const startTZ = times[0];
    const endTZ = moment
      .utc(times[times.length - 1])
      .tz(selectedTimezone?.timezoneId ?? "Etc/UTC")
      .endOf("day")
      ?.valueOf();

    setIsLoading(true);
    const response = await AmplifyAPIWrapper.Get(`/availablePairings/${crewMember._id}?start=${startTZ}&end=${endTZ}`);
    const newPairings = Array.isArray(response) && response.length > 0 ? response : [];
    setPairings(newPairings);
    setIsLoading(false);
  }, [crewMember, times, selectedTimezone]);

  useEffect(() => {
    const getPairingDescriptionSetting = async (): Promise<void> => {
      const descriptionSetting = await getClientSetting(ClientSettingName.PairingDescription);
      setDescSetting(descriptionSetting?.setValue?.enabled ?? true);
    };
    getPairingDescriptionSetting();
  }, []);

  // Retrieve crew pairings on load.
  useEffect(() => {
    getAvailableCrewPairingsAction();
  }, [getAvailableCrewPairingsAction]);

  const addPairingToMember = (): void => {
    dispatch(closeContextMenu(id));
    dispatch(
      openWindow(
        "New Pairing",
        "PairingEditorWindow",
        path,
        {
          pairingId: `new_${v1()}`,
          crewMember: crewMember,
          selectedTime: times[0],
        },
        "context"
      )
    );
  };

  const asssignPairingToCrew = useCallback(
    async (pairingId: string, shortCode: string): Promise<void> => {
      console.log("Assigning Pairing to Crew Member", pairingId, shortCode);
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Checking Pairing Assignment",
        errorMessage: "Error: Unable to Assign Pairing",
      };
      const response: any = await AmplifyAPIWrapper.Put(
        `/assignCrewToPairing/${pairingId}/crewId/${crewMember._id}`,
        null,
        notifications
      );
      if (response?.errors && response.errors.length > 0) {
        //Alert user!!
        setActiveEventModal?.({
          type: EventModalTypes.PairingError,
          modalProps: {
            errors: response.errors,
            pairingId: pairingId,
            pairingShortCode: shortCode,
            crewMember: crewMember,
            pairingErrorTypes: PairingErrorTypes.AvailablePairingAssignmentError,
          },
        });
      } else if (response?._id) {
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "success",
            message: "Pairing Assignment Successful",
          })
        );
        getAvailableCrewPairingsAction();
      }
    },
    [crewMember, dispatch, getAvailableCrewPairingsAction, setActiveEventModal]
  );

  return (
    <Grid container spacing={0}>
      {isLoading ? (
        <Grid item xs={12}>
          <LoadingWrapper message="loading available pairings" messageSx={{ fontSize: "0.8em" }} />
        </Grid>
      ) : (
        <>
          <Grid item xs={12} sx={{ pb: 0.5 }}>
            <Button
              variant="contained"
              size="small"
              fullWidth
              sx={{ fontSize: "0.7em" }}
              color="success"
              onClick={() => addPairingToMember()}
            >
              Create New Pairing
            </Button>
          </Grid>

          {pairingsByDate.map((date: IPairingsOnDate) => {
            return (
              <Fragment key={date.dateKey}>
                {isMultiDay && (
                  <Grid item xs={12}>
                    <Divider textAlign="left" sx={{ my: 0.5 }}>
                      <Box
                        sx={{
                          fontSize: "0.7em",
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                          color: "text.secondary",
                        }}
                      >
                        {date.label}
                      </Box>
                    </Divider>
                  </Grid>
                )}
                {date.pairings.map((pairing: IPairing) => {
                  return (
                    <Grid
                      item
                      container
                      xs={12}
                      key={pairing._id}
                      style={{
                        display: "flex",
                        fontSize: "0.8em",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                      columnSpacing={1}
                      onClick={() => asssignPairingToCrew(pairing._id, pairing.shortId)}
                    >
                      <Grid item xs="auto">
                        Assign
                      </Grid>
                      <Grid item xs="auto">
                        <ShortCodeChip code={pairing.shortId} itemId={pairing._id} type="pairing" />
                      </Grid>
                      {descSetting && (
                        <Grid item xs>
                          <ShortDescription description={pairing?.shortDescription} />
                        </Grid>
                      )}
                    </Grid>
                  );
                })}
              </Fragment>
            );
          })}
        </>
      )}
    </Grid>
  );
};
